/* GlobalVsHumanRematch — App-level listener that shows the amber "Desforra"
 * dialog anywhere in the app when a rematch invite arrives, and adopts the
 * subsequent `match_start` so the duel starts even if the recipient was still
 * on the GamePlay result screen when their opponent clicked Desforra.
 *
 * VsHumano.jsx also handles these two message types when it's mounted; both
 * handlers set the same sessionStorage keys and navigate to the same URL, so
 * running both is idempotent.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw, X } from "lucide-react";
import { useVsHumanSocket } from "@/lib/vsHumanSocket";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";

export default function GlobalVsHumanRematch() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null); // {invite_id, from:{user_id,name,avatar_cfg}}
  const [timer, setTimer] = useState(0);
  const consumedRef = useRef(new Set()); // dedupe match_start session_ids

  const handleMessage = useMemo(() => (msg) => {
    // Only care about REMATCH invites. Normal invites are handled by
    // /vs-humano's own dialog while the user is on that page.
    if (msg.type === "invite_received" && msg.rematch) {
      setTimer(10);
      setInvite(msg);
      return;
    }
    if (msg.type === "invite_cancelled" && invite?.invite_id === msg.invite_id) {
      setInvite(null);
      setTimer(0);
      return;
    }
    // Global match_start: adopt session + navigate to /jogar so the duel
    // starts even if the peer was on the GamePlay result screen or somewhere
    // else in the app when the rematch was accepted.
    if (msg.type === "match_start") {
      const sid = msg.session_id;
      if (!sid || consumedRef.current.has(sid)) return;
      consumedRef.current.add(sid);
      try {
        // These MUST already be in sessionStorage from the previous duel; we
        // only overwrite what the server tells us (level + session payload).
        const matchLevel = Number(msg.level) || Number(sessionStorage.getItem("player_level") || 1);
        sessionStorage.setItem("player_level", String(matchLevel));
        sessionStorage.setItem("vsh_session", JSON.stringify({
          session_id: msg.session_id,
          game: msg.game,
          questions: msg.questions,
          sequence: msg.sequence || [],
          cards: msg.cards || [],
          rounds: msg.rounds || [],
          supert_letters: msg.supert_letters,
          opponent: msg.opponent,
          opponent_level: msg.opponent_level ?? null,
          opponent_tier_id: msg.opponent_tier_id || null,
        }));
      } catch { /* ignore storage errors */ }
      // Navigate — no-op if we're already on the right URL. The subsequent
      // GamePlay mount reads sessionStorage and starts the rematch.
      if (msg.game?.id) {
        nav(`/jogar/${msg.game.id}?mode=vs_human&vsh=${msg.session_id}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite]);

  const { send } = useVsHumanSocket(handleMessage);

  // Countdown for the invite. Same UX as /vs-humano's normal invite dialog.
  // Ticks 10 → 0. When it reaches 0 and the user hasn't reacted, auto-decline
  // the invite AND navigate to /vs-humano — the user requested that the
  // dialog never leaves them stranded on the previous duel's result screen
  // (previous bug: the effect returned early at 0 → dialog froze at "0" with
  // both buttons still visible but no auto-action).
  useEffect(() => {
    if (!invite) return undefined;
    if (timer <= 0) {
      try { send({ type: "invite_response", invite_id: invite.invite_id, accept: false }); } catch { /* */ }
      setInvite(null);
      nav("/vs-humano");
      return undefined;
    }
    const tid = setTimeout(() => setTimer((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(tid);
  }, [invite, timer, send, nav]);

  const respond = (accept) => {
    if (!invite) return;
    try { send({ type: "invite_response", invite_id: invite.invite_id, accept }); } catch { /* */ }
    setInvite(null);
    setTimer(0);
  };

  return (
    <AnimatePresence>
      {invite && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-gradient-to-br from-amber-900/80 via-orange-900/80 to-rose-900/80 grid place-items-center p-4"
          data-testid="global-rematch-dialog"
        >
          <motion.div
            initial={{ scale: 0.7, rotate: -3 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-3xl shadow-[0_25px_60px_-15px_rgba(217,119,6,0.6)] w-full max-w-md p-8 text-center ring-4 ring-amber-400"
          >
            <div className="w-20 h-20 mx-auto mb-2 rounded-full overflow-hidden bg-amber-200 ring-4 ring-amber-400 grid place-items-center">
              <AvatarSVG cfg={invite.from?.avatar_cfg || DEFAULT_AVATAR} size={76} />
            </div>
            <div className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700 mb-1">
              {invite.from?.name}
            </div>
            <h3 className="text-4xl md:text-5xl font-[Fredoka] font-black text-amber-900 leading-none">
              {invite.sender_won ? t("vsh_playagain_prompt") : t("vsh_rematch_prompt")}
            </h3>
            <p className="text-amber-800 mt-2 text-sm">
              {invite.sender_won
                ? t("vsh_playagain_prompt_sub", { name: invite.from?.name || "" })
                : t("vsh_rematch_prompt_sub", { name: invite.from?.name || "" })}
            </p>

            {/* Big circular countdown */}
            <div className="relative w-32 h-32 mx-auto mt-5">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
                <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="6" />
                <circle
                  cx="50" cy="50" r="46" fill="none"
                  stroke={timer <= 3 ? "#e11d48" : "#f59e0b"}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 46}
                  strokeDashoffset={2 * Math.PI * 46 * (1 - Math.max(0, Math.min(timer, 10)) / 10)}
                  style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }}
                />
              </svg>
              <div
                className={`absolute inset-0 grid place-items-center font-[Fredoka] font-black tabular-nums ${timer <= 3 ? "text-rose-600" : "text-amber-700"}`}
                data-testid="global-rematch-countdown"
              >
                <div className="text-5xl leading-none">{timer}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <Button
                onClick={() => respond(true)}
                className="h-14 text-lg bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold shadow-md ring-2 ring-amber-300"
                data-testid="global-rematch-accept"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                {invite.sender_won ? t("vsh_playagain_accept") : t("vsh_rematch_accept")}
              </Button>
              <Button
                onClick={() => respond(false)}
                variant="outline"
                className="h-14 text-base rounded-full font-bold border-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                data-testid="global-rematch-decline"
              >
                <X className="w-5 h-5 mr-1" />
                {invite.sender_won ? t("vsh_playagain_decline") : t("vsh_rematch_decline")}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
